import React from 'react'
import { useAuth } from '@/hooks/useAuth'
import DashboardLayout from '@/components/DashboardLayout'
import { Shield, AlertTriangle } from 'lucide-react'

interface ProtectedRevFinOpsRouteProps {
  children: React.ReactNode
}

/**
 * ProtectedRevFinOpsRoute - Access control wrapper for RevFinOps pages
 *
 * Enforces admin-only access to RevFinOps financial data.
 * Non-admin users see an access denied screen with contact information.
 * All unauthorized access attempts are logged to the console.
 *
 * @param children - The protected page content to render if authorized
 */
export function ProtectedRevFinOpsRoute({ children }: ProtectedRevFinOpsRouteProps) {
  const { user } = useAuth()

  // Early return for access check - prevents data loading
  if (!user || user.role !== 'admin') {
    // Log unauthorized access attempt
    // eslint-disable-next-line no-console
    console.warn('Unauthorized RevFinOps access attempt', {
      user: user?.email || 'not logged in',
      userId: user?.id || null,
      role: user?.role || 'none',
      attemptedRoute: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    })

    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in-up">
          <Shield className="h-16 w-16 text-brand-red animate-pulse" />
          <h1 className="text-3xl font-bold text-white font-display">
            Acesso Restrito
          </h1>
          <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
            Esta página contém informações financeiras sensíveis do RevFinOps e está disponível apenas para administradores.
          </p>
          <div className="card-hover max-w-md">
            <div className="flex items-center space-x-3 mb-3">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <span className="text-white font-display font-semibold">
                Permissão Necessária
              </span>
            </div>
            <p className="text-brand-gray font-body text-sm">
              Entre em contato com um administrador se você precisa acessar o módulo RevFinOps.
            </p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // User is authorized - render protected content
  return <>{children}</>
}
